import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MovdInput from '../design-system/MovdInput.jsx';
import MovdButton from '../design-system/MovdButton.jsx';
import MovdNotice from '../design-system/MovdNotice.jsx';
import { movdTokens } from '../design-system/movdTokens.js';

const cardStyle = { padding: 24, borderRadius: 8, border: '1px solid #dce9e7', background: '#fff' };

const contactMethods = [
  { label: 'Email', details: 'support@movd.ca', href: 'mailto:support@movd.ca', description: 'For questions about your move or account.' },
  { label: 'Partner support', details: 'Become a partner', href: '/become-partner', description: 'For moving companies creating a MOVD profile.' },
  { label: 'Move planning', details: 'Start your move', href: '/', description: 'Build your route and move details to get started.' },
];

export default function ContactPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: 'General', message: '' });
  const [notice, setNotice] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const handleInputChange = e => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async e => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setNotice({ severity: 'error', text: 'Please fill in all required fields' });
      return;
    }
    setSubmitting(true);
    await new Promise(r => setTimeout(r, 900));
    setSubmitting(false);
    setNotice({ severity: 'success', text: "Thank you for your message! We'll get back to you within 24 hours." });
    setFormData({ name: '', email: '', phone: '', subject: 'General', message: '' });
  };

  return (
    <main style={{ minHeight: '100vh', background: '#f8fafb', color: '#102449' }}>
      <section style={{ background: '#e5f7f4', borderBottom: '1px solid #d0e9e4', padding: 'clamp(48px, 7vw, 76px) 20px' }}><div style={{ maxWidth: 1120, margin: '0 auto' }}><p style={eyebrowStyle}>GET IN TOUCH</p><h1 style={heroTitleStyle}>How can we help?</h1><p style={heroCopyStyle}>Send us a note about your move, your booking, or joining MOVD as a moving company.</p></div></section>

      <section style={{ maxWidth: 1120, margin: '0 auto', padding: 'clamp(40px, 6vw, 64px) 20px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 40 }}>
          {contactMethods.map(method => (
            <div key={method.label} style={cardStyle}>
              <p style={{ margin: '0 0 16px', color: '#008d7b', fontWeight: 900, fontSize: '.75rem', letterSpacing: '.1em' }}>{method.label.toUpperCase()}</p>
              <a href={method.href} style={{ display: 'block', marginBottom: 8, fontWeight: 900, color: '#102449', fontSize: '1.05rem', textDecoration: 'none' }}>{method.details}</a>
              <p style={{ margin: 0, color: '#52637c', fontSize: '.9rem', lineHeight: 1.5 }}>{method.description}</p>
            </div>
          ))}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, alignItems: 'start' }}>
          <div style={cardStyle}>
            <p style={{ margin: '0 0 8px', color: '#008d7b', fontWeight: 900, fontSize: '.75rem', letterSpacing: '.1em' }}>SEND A MESSAGE</p>
            <h2 style={{ margin: '0 0 20px', fontWeight: 900, fontSize: '1.8rem' }}>Tell us what you need.</h2>
            <form onSubmit={handleSubmit} style={{ display: 'grid', gap: 16 }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <MovdInput label="Full name" name="name" value={formData.name} onChange={handleInputChange} required />
                <MovdInput label="Email address" name="email" type="email" value={formData.email} onChange={handleInputChange} required />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <MovdInput label="Phone number" name="phone" value={formData.phone} onChange={handleInputChange} />
                <label style={{ display: 'grid', gap: 8, color: movdTokens.color.tealSoft, fontSize: 11, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                  <span>Subject</span>
                  <select name="subject" value={formData.subject} onChange={handleInputChange} style={{ minHeight: 52, borderRadius: movdTokens.radius.control, border: '1px solid rgba(0,0,0,.15)', padding: '0 12px', font: 'inherit', fontSize: 15, fontWeight: 600, textTransform: 'none', color: movdTokens.color.ink }}>
                    <option value="General">General inquiry</option>
                    <option value="Booking Help">Booking help</option>
                    <option value="Partner Inquiry">Partner inquiry</option>
                    <option value="Complaint">Complaint</option>
                  </select>
                </label>
              </div>
              <label style={{ display: 'grid', gap: 8, color: movdTokens.color.tealSoft, fontSize: 11, fontWeight: 900, letterSpacing: '.14em', textTransform: 'uppercase' }}>
                <span>Message</span>
                <textarea name="message" value={formData.message} onChange={handleInputChange} required rows={4} placeholder="Tell us how we can help you..." style={{ borderRadius: movdTokens.radius.control, border: '1px solid rgba(0,0,0,.15)', padding: 12, font: 'inherit', fontSize: 15, fontWeight: 600, textTransform: 'none', color: movdTokens.color.ink, resize: 'vertical' }} />
              </label>
              <MovdButton type="submit" loading={submitting}>Send message</MovdButton>
            </form>
          </div>

          <div style={cardStyle}>
            <p style={{ margin: '0 0 8px', color: '#008d7b', fontWeight: 900, fontSize: '.75rem', letterSpacing: '.1em' }}>PLANNING A MOVE?</p>
            <h3 style={{ margin: '0 0 12px', fontWeight: 900, fontSize: '1.25rem' }}>Start with the details.</h3>
            <p style={{ margin: '0 0 16px', color: '#52637c', fontSize: '.94rem', lineHeight: 1.6 }}>Build your move to begin reviewing options that fit your route, date, and move details.</p>
            <MovdButton onClick={() => navigate('/')} style={{ width: '100%' }}>Start your move</MovdButton>
          </div>
        </div>
      </section>

      {notice && <div style={{ position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 1500, minWidth: 280 }}><MovdNotice tone={notice.severity}>{notice.text}</MovdNotice></div>}
    </main>
  );
}

const eyebrowStyle = { margin: '0 0 12px', color: '#008d7b', fontWeight: 900, letterSpacing: '.1em', fontSize: '.75rem' };
const heroTitleStyle = { margin: '0 0 16px', color: '#102449', fontWeight: 900, fontSize: 'clamp(2.5rem, 5vw, 4.25rem)', lineHeight: .98, letterSpacing: 0 };
const heroCopyStyle = { margin: 0, maxWidth: 580, color: '#405875', fontSize: '1.08rem', lineHeight: 1.6 };
